import { useEffect, useState } from "react";
import { SquareCatalogProduct } from "../types/SquareCatalog";
import { catalogUrl } from "../utils/squareWorker";

type CatalogState = {
  products: SquareCatalogProduct[];
  isLoading: boolean;
  error: string | null;
};

export function useSquareCatalog(): CatalogState {
  const [state, setState] = useState<CatalogState>({ products: [], isLoading: true, error: null });

  useEffect(() => {
    const controller = new AbortController();

    async function loadCatalog() {
      try {
        const response = await fetch(catalogUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error("Catalog could not be loaded.");
        }

        const payload: unknown = await response.json();
        const records = Array.isArray(payload) ? payload : getRecords(payload);
        setState({ products: records.map(normalizeProduct).filter(isCatalogProduct), isLoading: false, error: null });
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setState({ products: [], isLoading: false, error: "The live catalog is unavailable right now. Please try again shortly." });
      }
    }

    void loadCatalog();
    return () => controller.abort();
  }, []);

  return state;
}

function getRecords(payload: unknown): unknown[] {
  if (!isRecord(payload)) {
    return [];
  }

  const possibleRecords = payload.items ?? payload.products ?? payload.data ?? payload.catalog;
  return Array.isArray(possibleRecords) ? possibleRecords : [];
}

function normalizeProduct(record: unknown): SquareCatalogProduct {
  const source = isRecord(record) ? record : {};
  const variationId = readString(source.variationId) ?? readString(source.id) ?? "";
  const name = readString(source.name) ?? readString(source.itemName) ?? "Untitled product";
  const price = readPrice(source);

  return {
    itemId: readString(source.itemId) ?? "",
    variationId,
    name,
    variationName: readString(source.variationName) ?? readString(source.variation) ?? "Standard",
    description: readString(source.description) ?? "",
    imageIds: readStringArray(source.imageIds),
    imageUrl: readImageUrl(source),
    priceCents: price.cents,
    currency: price.currency,
    inventory: readNumber(source.inventory) ?? 0,
  };
}

function readPrice(source: Record<string, unknown>): { cents: number | null; currency: string } {
  const directPrice = readNumber(source.priceCents) ?? readNumber(source.price) ?? readNumber(source.amount);
  if (directPrice !== null) {
    return { cents: directPrice, currency: readString(source.currency) ?? "USD" };
  }

  const money = isRecord(source.priceMoney) ? source.priceMoney : isRecord(source.price_money) ? source.price_money : null;
  return {
    cents: money ? readNumber(money.amount) : null,
    currency: money ? readString(money.currency) ?? "USD" : "USD",
  };
}

function readImageUrl(source: Record<string, unknown>): string | null {
  const direct = readString(source.imageUrl) ?? readString(source.imageURL) ?? readString(source.image);
  if (direct) {
    return direct;
  }

  const urlList = readStringArray(source.imageUrls);
  if (urlList.length > 0) {
    return urlList[0];
  }

  if (Array.isArray(source.images)) {
    for (const image of source.images) {
      if (typeof image === "string" && image.trim()) {
        return image;
      }
      if (isRecord(image)) {
        const url = readString(image.url) ?? (isRecord(image.image_data) ? readString(image.image_data.url) : null);
        if (url) {
          return url;
        }
      }
    }
  }

  return null;
}

function isCatalogProduct(product: SquareCatalogProduct): boolean {
  return product.variationId.length > 0;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function readNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
